<template>
  <div id="panelChart9" class="panel panel-default">
    <div class="panel-title top-margin">Note list</div>

    <div class="line-separator"></div>

    <bulk-redemption
      v-if="isBulkRedemptionVisible"
      :accountId="accountId"
      @close="closeBulkRedemption"
      @completed="onBulkRedemptionCompleted" />

    <bulk-transfer
      v-if="isBulkTransferVisible"
      :accountId="accountId"
      @close="closeBulkTransfer"
      @completed="onBulkTransferCompleted" />

    <div class="row bottom-margin">
      <div class="col-lg-6">
        <button type="button"
          class="btn btn-default btn-lg top-margin"
          v-on:click="goToPurchaseInvestmentNote()"
          v-bind:disabled="disableInvestment"
        >
          Make an investment
        </button>

        <button type="button"
          class="btn btn-danger btn-lg top-margin ml-sm"
          v-on:click="openBulkRedemption()"
          v-bind:disabled="!hasOpenNotes"
          title="Redeem every open note on this account in one request"
        >
          Redeem/approve all open notes
        </button>

        <button type="button"
          class="btn btn-danger btn-outline btn-lg top-margin ml-sm"
          v-on:click="openBulkTransfer()"
          v-bind:disabled="!hasTransferableNotes"
          title="Transfer every note on this account to another account in one request"
        >
          Transfer all notes
        </button>
      </div>

      <div class="col-lg-6 text-right account-balance-column">
        <br class="panel-subtitle"> Current account balance: <strong class="amountBalance">{{formatAmount(currentAmountBalance)}}</strong>
      </div>
    </div>

    <div class="row top-margin" v-if="notes.length === 0">
      <div class="col-lg-12">
        <div class="alert alert-info alert-dismissible" role="alert">
          No notes were found.
        </div>
      </div>
    </div>

    <div class="row mt-lg mb-lg">
      <div class="col-lg-12">
        <div v-if="notes.length > 0"
          v-bind:class="{'table-fixed-header--md table-fixed-column cols-11': notes.length > 6, 'table-responsive': true, 'bottom-margin': true}">
          <table class="table table-striped">
            <thead>
              <tr>
                <th class="text-center sticky-col">
                  <div @click="sortNotesById('noteId')" class="note-sort-title small-container">
                    <span>Note number</span>
                    <em :class="sortIconStyle('noteId')"></em>
                  </div>
                </th>
                <th class="text-right">
                  <div @click="sortNotes('originalBalance')" class="note-sort-title small-container">
                    <span>Original amount</span>
                    <em :class="sortIconStyle('originalBalance')"></em>
                  </div>
                </th>
                <th class="text-right">
                  <div @click="sortNotes('currentBalance')" class="note-sort-title small-container">
                    <span>Current amount</span>
                    <em :class="sortIconStyle('currentBalance')"></em>
                  </div></th>
                <th class="text-center">
                  <div @click="sortNotes('interestRate')" class="note-sort-title small-container">
                    <span class="small-field">Rate</span>
                    <em :class="sortIconStyle('interestRate')"></em>
                  </div>
                </th>
                <th class="text-center">
                  <div @click="sortNotes('interestTerm')" class="note-sort-title small-container">
                    <span class="small-field">Term</span>
                    <em :class="sortIconStyle('interestTerm')"></em>
                  </div>
                </th>
                <th class="text-center">
                  <div @click="sortNotes('purchasedOn')" class="note-sort-title small-container">
                    <span class="small-field">Purchase date</span>
                    <em :class="sortIconStyle('purchasedOn')"></em>
                  </div>
                </th>
                <th class="text-center">
                  <div @click="sortNotes('openedOn')" class="note-sort-title small-container">
                    <span class="small-field">Open date</span>
                    <em :class="sortIconStyle('openedOn')"></em>
                  </div>
                </th>
                <th class="text-center">
                  <div @click="sortNotes('maturityDate')" class="note-sort-title small-container">
                    <span class="small-field">Maturity date</span>
                    <em :class="sortIconStyle('maturityDate')"></em>
                  </div>
                </th>
                <th class="text-center">
                  <div @click="sortByStatus()" class="note-sort-title small-container">
                    <span class="small-field">Status</span>
                    <em :class="sortIconStyle('status')"></em>
                  </div>
                </th>
                <th class="text-center">
                   <div @click="sortNotesById('legacyNoteID')" class="note-sort-title large-container">
                    <span>Legacy note number</span>
                    <em :class="sortIconStyle('legacyNoteID')"></em>
                  </div>
                </th>
                <th class="text-center">View details</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(note, index) in orderedNotes" :key="index">
                <td class="text-center sticky-col">{{ note.noteId }}</td>
                <td class="text-right">{{ formatAmount(note.originalBalance) }}</td>
                <td class="text-right">{{ formatAmount(note.currentBalance) }}</td>
                <td class="text-center">{{ formatRate(note.interestRate) }}</td>
                <td class="text-center">{{ formatTerm(note.interestTerm) }}</td>
                <td class="text-center">{{ formatDate(note.purchasedOn) }}</td>
                <td class="text-center">{{ formatDate(note.openedOn) }}</td>
                <td class="text-center">{{ formatDate(note.maturityDate) }}</td>
                <td class="text-center">
                  <span class="label label-danger" v-if="note.status == 'cancelled'">Cancelled</span>
                  <span class="label label-danger" v-if="note.status == 'failed'">Failed</span>
                  <span class="label label-danger" v-if="note.status == 'closed'">Closed</span>
                  <span class="label label-warning" v-if="note.status == 'pending'">Pending</span>
                  <span class="label label-success" v-if="note.status == 'open'">Open</span>
                </td>
                <td class="text-center">{{ note.legacyNoteID }}</td>
                <td class="text-center">
                  <button type="button" class="btn btn-details btn-outline" v-on:click="goToViewDetail(note)">View details</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="row top-margin" v-if="displayAlertForUpcomingTransactionsWithAction">
      <div class="col-lg-12">
        <div class="alert alert-info" role="alert">
          <h4><em class="icon-info pr-sm"></em>Please note this account has upcoming transactions that have received action.</h4>
          <p>Click <a href @click.prevent="gotToUpcomingTransactionsSegment">here</a> to review the actions received for the transactions.</p>
        </div>
      </div>
    </div>

    <div class="row top-margin" v-if="displayAlertForUpcomingTransactionsWithNoAction">
      <div class="col-lg-12">
        <div class="alert alert-info" role="alert">
          <h4><em class="icon-info pr-sm"></em>Please note this account has upcoming transactions that require action.</h4>
          <p>Click <a href @click.prevent="gotToUpcomingTransactionsSegment">here</a> to see the transactions that require action.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {
    mapActions,
  } from 'vuex';

  import { Events } from '@/data/static/events';
  import BulkRedemption from '@/components/modals/BulkRedemption';
  import BulkTransfer from '@/components/modals/BulkTransfer';

  import {
    formatAmount,
    formatDate,
    formatRate,
    formatTerm,
  } from '@/util';

  const mix = {
    methods: {
      formatAmount,
      formatDate,
      formatRate,
      formatTerm,
    },
  };

  export default {
    name: 'AccountNotes',
    components: {
      'bulk-redemption': BulkRedemption,
      'bulk-transfer': BulkTransfer,
    },
    mounted() {
      this.initialize();
    },
    data() {
      return {
        currentAmountBalance: '',
        notes: [],
        orderedNotes: [],
        sortProperty: '',
        sortToggle: 1,
        isBulkRedemptionVisible: false,
        isBulkTransferVisible: false,
      };
    },
    computed: {
      // The modal itself reports which notes actually qualify; this only keeps
      // the button from opening it on an account with nothing to redeem.
      hasOpenNotes() {
        return this.notes.some(note => note.status === 'open');
      },
      // Deliberately looser than hasOpenNotes: the single-note transfer flow
      // gates on a pending transfer, never on status, so an account holding
      // only closed notes still has something to move.
      hasTransferableNotes() {
        return this.notes.length > 0;
      },
    },
    methods: {
      ...mapActions('account', [
        'getAccountNotes',
        'getCurrentAmountBalance',
      ]),
      async initialize() {
        this.$emit(Events.NOTES_LOADING_STARTED);
        const notes = await this.getAccountNotes(this.accountId);

        const amountBalanceResponse = await this.getCurrentAmountBalance({
          accountId: this.accountId,
        });

        this.currentAmountBalance = amountBalanceResponse.currentAmountBalance;
        this.notes = notes.slice(0);
        this.sortByStatus();
        this.$emit(Events.NOTES_LOADING_SUCCEED);
      },
      goToPurchaseInvestmentNote() {
        this.$emit(Events.REDIRECT_TO_PURCHASE_INVESTMENT_NOTE_VIEW);
      },
      openBulkRedemption() {
        this.isBulkRedemptionVisible = true;
      },
      closeBulkRedemption() {
        this.isBulkRedemptionVisible = false;
      },
      // Balances and note statuses change once a batch is requested or approved,
      // so the list is reloaded rather than left showing pre-redemption state.
      onBulkRedemptionCompleted() {
        this.initialize();
      },
      openBulkTransfer() {
        this.isBulkTransferVisible = true;
      },
      closeBulkTransfer() {
        this.isBulkTransferVisible = false;
      },
      // Matters more here than for a redemption: approved notes belong to
      // another account now and leave this list entirely.
      onBulkTransferCompleted() {
        this.initialize();
      },
      goToViewDetail(note) {
        this.$emit(Events.REDIRECT_TO_NOTE_DETAILS_VIEW, note);
      },
      gotToUpcomingTransactionsSegment() {
        this.$emit(Events.GO_TO_UPCOMING_TRANSACTIONS_SEGMENT);
      },
      sortNotesById(property) {
        this.sortToggle = this.sortProperty === property ? this.sortToggle * -1 : 1;

        this.orderedNotes = this.notes.sort((a, b) => {
          if (!a[property] && !b[property]) {
            return 0;
          }
          if (!b[property]) {
            return 1 * this.sortToggle;
          }
          if (!a[property]) {
            return -1 * this.sortToggle;
          }
          return (
            a[property].localeCompare(b[property], 'en', { numeric: true }) *
            this.sortToggle
          );
        });

        this.sortProperty = property;
      },
      sortByStatus() {
        this.sortToggle = this.sortProperty === 'status' ? this.sortToggle * -1 : -1;

        this.orderedNotes = this.notes.sort((a, b) => {
          if (a.status < b.status || (!a.status && b.status)) {
            return -1 * this.sortToggle;
          }
          if (a.status > b.status || (a.status && !b.status)) {
            return 1 * this.sortToggle;
          }
          if (a.purchasedOn < b.purchasedOn || (!a.purchasedOn && b.purchasedOn)) {
            return 1;
          }
          if (a.purchasedOn > b.purchasedOn || (a.purchasedOn && !b.purchasedOn)) {
            return -1;
          }
          return 0;
        });

        this.sortProperty = 'status';
      },
      sortNotes(property) {
        this.sortToggle = this.sortProperty === property ? this.sortToggle * -1 : 1;

        this.orderedNotes = this.notes.sort((a, b) => {
          if (a[property] < b[property] || (!a[property] && b[property])) {
            return -1 * this.sortToggle;
          }
          if (a[property] > b[property] || (a[property] && !b[property])) {
            return 1 * this.sortToggle;
          }
          return 0;
        });

        this.sortProperty = property;
      },
      sortIconStyle(property) {
        if (property === this.sortProperty) {
          return `fa fa-sort-${this.sortToggle === 1 ? 'asc' : 'desc'}`;
        }
        return 'fa fa-sort';
      },
    },
    mixins: [mix],
    props: [
      'accountId',
      'disableInvestment',
      'displayAlertForUpcomingTransactionsWithAction',
      'displayAlertForUpcomingTransactionsWithNoAction',
    ],
  };
</script>

<style scoped>
  .panel-title {
    font-size: 20px;
    margin-bottom: 5px;
  }
  .btn-trash {
    border: 1px solid #DCE0E5;
    background: white;
  }
  .delete-icon {
    color: #f05050;
    margin-top: -3px;
  }
  .radio-item label {
    color: #666;	    color: #666;
    font-weight: normal;
    padding-left: 25px;
    padding-top: 5px;
    cursor: pointer;
  }
  .radio-item {
    height: 25px;
  }
  .radio-item label:before {
    content: " ";
    display: inline-block;
    position: absolute;
    top: 0;
    left: 0px;
    margin: 0 5px 0 0;
    width: 20px;
    height: 20px;
    border-radius: 11px;
    background-color: transparent;
  }
  .radio-item-unselected label:before {
    border: 2px solid #d3d3d3;
  }
  .radio-item label:after {
    border-radius: 5px;
    width: 10px;
    height: 10px;
    position: absolute;
    top: 5px;
    left: 5px;
    margin: auto;
    content: "";
    display: block;
    background-color: #127285;
  }
  .radio-item.radio-item-unselected label:after {
    display: none;
  }
  table {
    border-collapse: collapse;
  }
  th {
    padding: 14px 8px;
    color: #888;
    border-bottom-width: 1px;
    vertical-align: bottom;
    border-bottom: 1px solid #eee;
    border-top: 0;
    line-height: 1.52857143;
    font-weight: normal;
  }
  td {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
    height: 60px;
  }
  .table-striped>tbody>tr:nth-of-type(odd) {
    background-color: #fafbfc;
  }
  .panel-title {
    font-size: 20px;
  }
  .panel-default {
    padding: 30px 40px;
  }
  .line-separator {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
  }
  tbody tr td:last-child {
    padding-top: 10px;
    padding-right: 20px;
  }

  .table-fixed-header--md tr th:last-child {
    margin-right: 12px;
    width: 10.4%;
  }

  @media only screen and (max-width: 1440px) {
    table {
      min-width: 1500px;
    }
  }

  @media only screen and (min-width: 1440px) {
    .table-fixed-header--md {
      overflow-x: auto;
      overflow-y: auto;
    }
  }

  @media only screen and (max-width: 1440px) {
    .sticky-col {
      position: sticky;
      left: 0;
      z-index: 1;
      background: white;
      border: 1px solid #DDD;
      border-left-color: transparent;
      border-bottom-color: transparent;
    }
    .table-fixed-column thead {
      position: sticky;
      top: 0;
      display: table-header-group;
      z-index: 10;
      background: white;
    }
    .table-fixed-column tbody {
      display: table-row-group;
    }
    .table-fixed-column tbody tr {
      position: relative;
    }
    .table-fixed-column thead .sticky-col {
      border-bottom-color: #DDD;
    }
    .table-fixed-column tbody .sticky-col {
      position: sticky;
      max-width: 9.05%;
      margin: 0;
      top: 0;
    }
    .table-fixed-column tbody tr:nth-child(odd) .sticky-col {
      background: #fafbfc;
    }
  }

  /* Fixed Header + fixed first column in chrome */
  @media screen and (-webkit-min-device-pixel-ratio:0) and (min-resolution:.001dpcm) and (max-width: 1440px) {
    .table-fixed-column thead {
      display: table;
    }
  }
  @media only screen and (max-width: 1440px) {
    .table-fixed-header--md tr td:last-child {
      padding-right: 10px;
    }
    .table-fixed-header--md tr th:last-child {
      padding-right: 10px;
      margin-right: 0;
      width: 9.1%;
    }
  }

  .note-sort-title {
    display: flex;
    align-items: center;
    user-select: none;
  }

  .note-sort-title:hover {
    cursor: pointer;
  }

  .fa {
    padding-left: 10px;
    margin-top: auto;
    margin-bottom: 3px;
  }

  .small-container {
    justify-content: center;
  }

  .large-container {
    justify-content: flex-end;
  }

  .large-container .fa {
    margin-bottom: 22px;
  }
</style>



// WEBPACK FOOTER //
// AccountNotes.vue?06c821b6